import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal } from '@viapeople/components'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import type { CalibrationParticipant } from '../types'

interface CalibrationRatingRowProps {
	participants: CalibrationParticipant[]
	rating: {
		componentId: string
		rowTitle: string
	}
	ratingElements?: Array<{
		id: string
		options?: Array<{
			label: string
			value: string
			numericValue?: number
		}>
	}>
}

export function CalibrationRatingRow({ participants, rating, ratingElements }: CalibrationRatingRowProps) {
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [selectedParticipant, setSelectedParticipant] = useState<CalibrationParticipant | null>(null)
	const { sessionId } = useParams<{ sessionId: string }>()
	const { showToast } = useToast()
	const queryClient = useQueryClient()

	// @ts-expect-error - TRPC mutation options type compatibility issue
	const updateRatingMutation = useMutation({
		...trpc.client.calibrations.updateRating.mutationOptions(),
		onSuccess: () => {
			showToast('Value successfully updated.')
			// Invalidate and refetch the calibration data to update the UI
			queryClient.invalidateQueries({
				queryKey: ['calibrations', 'getData', { sessionId: sessionId || '' }],
			})
		},
		onError: (error: unknown) => {
			showToast('Something went wrong, calibration was not submitted.', 'error')
			console.error('Error updating rating:', error)
		},
	})

	const handleRatingClick = (participant: CalibrationParticipant) => {
		setSelectedParticipant(participant)
		setIsModalOpen(true)
	}

	const handleCloseModal = () => {
		setIsModalOpen(false)
		setSelectedParticipant(null)
	}

	const renderRatingCell = (participant: CalibrationParticipant) => {
		// Find the rating for this component ID
		const participantRating = participant.calibratedRatings?.find((r) => r.componentId === rating.componentId)

		if (!participantRating || participantRating.numericValue === null || participantRating.numericValue === undefined) {
			// Show "NA" for missing ratings
			return (
				<button
					type='button'
					className='rounded-md border-neutral-200 border-1 px-5 py-3 cursor-pointer hover:bg-neutral-50 transition-colors'
					onClick={() => handleRatingClick(participant)}>
					NA
				</button>
			)
		}

		// Apply color coding based on numeric value
		const numericValue = participantRating.numericValue
		let colorClass = ''

		if (numericValue <= 3) {
			colorClass = 'bg-warning-100' // Warning color for 3 or less
		} else {
			colorClass = 'bg-success-200' // Success color for more than 3
		}

		return (
			<button
				type='button'
				className={`${colorClass} rounded-md border-neutral-200 px-5 py-3 cursor-pointer hover:opacity-80 transition-colors`}
				onClick={() => handleRatingClick(participant)}>
				{participantRating.calibratedRating}
			</button>
		)
	}

	const getRatingOptionStyle = (value: string | number) => {
		if (value === 'NA') {
			return 'rounded-md border-neutral-200 border-1 px-5 py-3 cursor-pointer hover:bg-neutral-50 transition-colors'
		}

		const numericValue = typeof value === 'string' ? Number.parseInt(value, 10) : value
		let colorClass = ''

		if (numericValue <= 3) {
			colorClass = 'bg-warning-100' // Warning color for 3 or less
		} else {
			colorClass = 'bg-success-200' // Success color for more than 3
		}

		return `${colorClass} rounded-md border-neutral-200 px-5 py-3 cursor-pointer hover:opacity-80 transition-colors`
	}

	// Get rating options from the rating elements configuration
	const currentRatingElement = ratingElements?.find((el) => el.id === rating.componentId)
	const ratingOptions = currentRatingElement?.options || [
		{ label: '1', value: '1', numericValue: 1 },
		{ label: '2', value: '2', numericValue: 2 },
		{ label: '3', value: '3', numericValue: 3 },
		{ label: '4', value: '4', numericValue: 4 },
		{ label: '5', value: '5', numericValue: 5 },
		{ label: 'NA', value: 'NA', numericValue: null },
	]

	const handleRatingSelection = async (option: { label: string; value: string; numericValue?: number | null }) => {
		if (!selectedParticipant || !sessionId) {
			console.error('Missing required data for rating update')
			return
		}

		try {
			await updateRatingMutation.mutateAsync({
				sessionId,
				participantId: selectedParticipant.participantId,
				componentId: rating.componentId,
				calibratedRating: option.label,
				numericValue: option.numericValue || undefined,
			})

			handleCloseModal()
		} catch (error) {
			console.error('Failed to update rating:', error)
		}
	}

	return (
		<>
			<div className='flex items-center text-center'>
				<div className='w-80 p-4 border-t border-neutral-200 flex-shrink-0 text-left'>{rating.rowTitle}</div>
				{participants.map((participant) => (
					<div key={participant.id} className='w-48 p-4 border-t border-neutral-200 flex-shrink-0'>
						{renderRatingCell(participant)}
					</div>
				))}
			</div>

			<Modal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				title={`${rating.rowTitle} - ${selectedParticipant?.firstName} ${selectedParticipant?.lastName}`}>
				<div className='space-y-4'>
					<div>
						<h3 className='text-lg font-medium text-neutral-800 mb-4'>Select Rating</h3>
						<div className='flex flex-wrap gap-3 justify-center'>
							{ratingOptions.map((option) => (
								<button
									type='button'
									key={option.value}
									className={getRatingOptionStyle(option.label)}
									onClick={() => handleRatingSelection(option)}>
									{option.label}
								</button>
							))}
						</div>
					</div>
				</div>
			</Modal>
		</>
	)
}
